import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { createInvoice, getInvoices, getInvoicingConfig } from '../lib/api';
import { toast } from 'sonner';
import { FileText, Loader2, Send } from 'lucide-react';

const emptyItem = () => ({
  description: 'Consulta médica',
  quantity: 1,
  price: '',
  product_key: '',
  unit_key: '',
  tax_rate: 0.16,
  tax_included: true,
});

export default function AdminInvoices() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [defaults, setDefaults] = useState({
    default_product_key: '85121500',
    default_unit_key: 'E48',
    facturapi_configured: false,
    facturapi_mode: 'unset',
  });
  const [form, setForm] = useState({
    customer_legal_name: '',
    customer_tax_id: '',
    customer_tax_system: '612',
    customer_zip: '',
    customer_email: '',
    customer_cfdi_use: 'G03',
    payment_form: '03',
    payment_method: 'PUE',
    items: [emptyItem()],
  });

  const reload = async () => {
    setLoading(true);
    try {
      const [invoices, cfg] = await Promise.all([
        getInvoices(50),
        getInvoicingConfig().catch(() => null),
      ]);
      setList(Array.isArray(invoices) ? invoices : []);
      if (cfg) {
        setDefaults({
          default_product_key: cfg.default_product_key || '85121500',
          default_unit_key: cfg.default_unit_key || 'E48',
          facturapi_configured: Boolean(cfg.facturapi_configured),
          facturapi_mode: cfg.facturapi_mode || 'unset',
          csd_has_certificate: Boolean(cfg.csd_has_certificate),
        });
        setForm((prev) => ({
          ...prev,
          items: prev.items.map((it) => ({
            ...it,
            product_key: it.product_key || cfg.default_product_key || '85121500',
            unit_key: it.unit_key || cfg.default_unit_key || 'E48',
          })),
        }));
      }
    } catch {
      toast.error('No se pudieron cargar las facturas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const updateItem = (idx, patch) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!defaults.facturapi_configured) {
      toast.error('Configure la Secret Key de Facturapi y los datos del emisor en Settings');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        customer_tax_id: form.customer_tax_id.trim().toUpperCase(),
        items: form.items.map((it) => ({
          description: it.description.trim(),
          quantity: Number(it.quantity) || 1,
          price: Number(it.price),
          product_key: (it.product_key || defaults.default_product_key).trim(),
          unit_key: (it.unit_key || defaults.default_unit_key).trim(),
          tax_rate: Number(it.tax_rate) || 0,
          tax_included: Boolean(it.tax_included),
        })),
      };
      const created = await createInvoice(payload);
      if (created?.email_warning) {
        toast.warning(`Timbrada, pero el email falló: ${created.email_warning}`);
      } else {
        toast.success(
          `Factura ${created.series || ''}${created.folio || ''} timbrada y enviada a ${created.customer_email}`,
        );
      }
      setForm((prev) => ({
        ...prev,
        customer_legal_name: '',
        customer_tax_id: '',
        customer_zip: '',
        customer_email: '',
        items: [
          {
            ...emptyItem(),
            product_key: defaults.default_product_key,
            unit_key: defaults.default_unit_key,
          },
        ],
      }));
      await reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo emitir la factura');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-semibold">Facturación electrónica</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Emitir CFDI, timbrar y enviar PDF/XML por email al cliente.
          {!defaults.facturapi_configured ? (
            <span className="text-amber-700"> Configure la Secret Key de Facturapi en Settings.</span>
          ) : (
            <span>
              {' '}
              Modo Facturapi: <strong>{defaults.facturapi_mode}</strong>
              {defaults.csd_has_certificate ? '' : ' · CSD pendiente en Settings'}
            </span>
          )}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Send className="w-5 h-5 text-primary" />
            Nueva factura
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nombre / razón social del cliente</Label>
                <Input
                  className="mt-1 h-11"
                  required
                  value={form.customer_legal_name}
                  onChange={(e) => setForm({ ...form, customer_legal_name: e.target.value })}
                />
              </div>
              <div>
                <Label>RFC</Label>
                <Input
                  className="mt-1 h-11 uppercase"
                  required
                  maxLength={13}
                  value={form.customer_tax_id}
                  onChange={(e) => setForm({ ...form, customer_tax_id: e.target.value })}
                />
              </div>
              <div>
                <Label>Régimen fiscal</Label>
                <Input
                  className="mt-1 h-11"
                  required
                  maxLength={3}
                  placeholder="612"
                  value={form.customer_tax_system}
                  onChange={(e) => setForm({ ...form, customer_tax_system: e.target.value })}
                />
              </div>
              <div>
                <Label>Código postal fiscal</Label>
                <Input
                  className="mt-1 h-11"
                  required
                  maxLength={5}
                  value={form.customer_zip}
                  onChange={(e) => setForm({ ...form, customer_zip: e.target.value })}
                />
              </div>
              <div>
                <Label>Email (envío de factura)</Label>
                <Input
                  type="email"
                  className="mt-1 h-11"
                  required
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Uso CFDI</Label>
                <Input
                  className="mt-1 h-11"
                  value={form.customer_cfdi_use}
                  onChange={(e) => setForm({ ...form, customer_cfdi_use: e.target.value })}
                  placeholder="G03"
                />
              </div>
              <div>
                <Label>Forma de pago</Label>
                <Input
                  className="mt-1 h-11"
                  value={form.payment_form}
                  onChange={(e) => setForm({ ...form, payment_form: e.target.value })}
                  placeholder="01 efectivo · 03 transferencia · 04 tarjeta"
                />
              </div>
              <div>
                <Label>Método de pago</Label>
                <Input
                  className="mt-1 h-11"
                  value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                  placeholder="PUE"
                />
              </div>
            </div>

            {form.items.map((it, idx) => (
              <div key={idx} className="rounded-lg border p-4 space-y-3 bg-muted/20">
                <p className="text-sm font-semibold">Concepto {idx + 1}</p>
                <div>
                  <Label>Descripción</Label>
                  <Textarea
                    className="mt-1"
                    rows={2}
                    required
                    value={it.description}
                    onChange={(e) => updateItem(idx, { description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Cantidad</Label>
                    <Input
                      className="mt-1 h-11"
                      type="number"
                      min="0.01"
                      step="0.01"
                      required
                      value={it.quantity}
                      onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Precio</Label>
                    <Input
                      className="mt-1 h-11"
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={it.price}
                      onChange={(e) => updateItem(idx, { price: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Clave SAT producto</Label>
                    <Input
                      className="mt-1 h-11"
                      value={it.product_key}
                      onChange={(e) => updateItem(idx, { product_key: e.target.value })}
                      placeholder={defaults.default_product_key}
                    />
                  </div>
                  <div>
                    <Label>IVA</Label>
                    <Input
                      className="mt-1 h-11"
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={it.tax_rate}
                      onChange={(e) => updateItem(idx, { tax_rate: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    items: [
                      ...prev.items,
                      {
                        ...emptyItem(),
                        product_key: defaults.default_product_key,
                        unit_key: defaults.default_unit_key,
                      },
                    ],
                  }))
                }
              >
                Agregar concepto
              </Button>
              <Button type="submit" disabled={submitting} className="rounded-full gap-2">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {submitting ? 'Timbrando…' : 'Timbrar y enviar'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Historial</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aún no hay facturas emitidas.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Folio</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">
                      {inv.series}
                      {inv.folio}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{inv.customer_legal_name}</div>
                      <div className="text-xs text-muted-foreground">{inv.customer_tax_id}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {inv.customer_email}
                      {inv.emailed_at ? (
                        <Badge variant="secondary" className="ml-2 text-[10px]">
                          Enviado
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {inv.total != null
                        ? `$${Number(inv.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={inv.status === 'valid' ? 'default' : 'outline'}>{inv.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {inv.created_at ? String(inv.created_at).slice(0, 19).replace('T', ' ') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
